import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useApiClient } from "../../lib/api";
import { useNotification } from "../../providers/notification-provider";

const hasPasswordPolicy = (value) => {
  const password = String(value || "");
  return password.length >= 8 && /[A-Za-z]/.test(password) && /\d/.test(password);
};

export default function UserProfileModal({ open, onClose, user }) {
  const api = useApiClient();
  const { showToast } = useNotification();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [feedback, setFeedback] = useState({ message: "", type: "" });
  const [passwordVisibility, setPasswordVisibility] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const showFeedback = (message, type) => {
    setFeedback({ message, type });
    showToast(message, type === "warning" ? "warning" : type === "error" ? "error" : "success");
  };

  useEffect(() => {
    if (!open) {
      setShowChangePassword(false);
      setIsSubmittingPassword(false);
      setFeedback({ message: "", type: "" });
      setPasswordVisibility({
        current_password: false,
        new_password: false,
        confirm_password: false
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    }
  }, [open]);

  const togglePasswordVisibility = (fieldName) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (isSubmittingPassword) return;

    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      showFeedback("Preencha todos os campos para alterar a senha.", "warning");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showFeedback("A confirmação da nova senha não confere.", "warning");
      return;
    }
    if (passwordForm.new_password === passwordForm.current_password) {
      showFeedback("A nova senha deve ser diferente da atual.", "warning");
      return;
    }
    if (!hasPasswordPolicy(passwordForm.new_password)) {
      showFeedback("A nova senha deve possuir no mínimo 8 caracteres, com letras e números.", "warning");
      return;
    }

    setIsSubmittingPassword(true);
    try {
      await api.post("/api/v1/auth/change-password", {
        currentPassword: passwordForm.current_password,
        newPassword: passwordForm.new_password,
      });
      showFeedback("Senha alterada com sucesso.", "success");
      setShowChangePassword(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      showFeedback(error.response?.data?.message || error.response?.data || "Erro ao alterar senha.", "error");
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative mx-auto max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-2xl sm:p-6">
          <div className="mb-6 flex items-start justify-between border-b border-gray-100 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-xl font-bold text-gray-900 sm:text-2xl">
                {user?.name || user?.email || "Meu Perfil"}
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">Informações da conta e segurança</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 transition hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {feedback.message && (
            <div className={`mb-4 rounded-md border p-4 text-sm ${feedback.type === "error"
              ? "border-red-200 bg-red-50 text-red-800"
              : feedback.type === "warning"
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-green-200 bg-green-50 text-green-800"}`}>
              {feedback.message}
            </div>
          )}

          {user ? (
            <div className="space-y-6">
               <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Nome</h4>
                  <p className="text-base font-medium text-gray-900">{user.name || "-"}</p>
                </div>
                <div>
                  <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Email</h4>
                  <p className="text-base font-medium text-gray-900 break-all">{user.email || "-"}</p>
                </div>
                <div>
                  <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Empresa Ativa</h4>
                  <p className="text-base font-medium text-gray-900">
                    {user.companyName || "N/A"}
                  </p>
                </div>
                 <div>
                  <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Tipo</h4>
                  <p className="text-base font-medium text-gray-900">{user.isAdminMaster ? "Super Admin" : "Usuário Padrão"}</p>
                </div>
                <div>
                  <h4 className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">Perfil</h4>
                  <p className="text-base font-medium text-gray-900">{user.role || "-"}</p>
                </div>
              </div>

               <div className="border-t border-gray-100 pt-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Segurança</h4>
                    <p className="text-xs text-gray-500">Altere sua senha de acesso ao sistema.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowChangePassword((prev) => !prev)}
                    className="inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100"
                  >
                    {showChangePassword ? "Cancelar alteração" : "Alterar senha"}
                  </button>
                </div>

                {showChangePassword && (
                  <form onSubmit={handlePasswordChange} className="mt-4 space-y-4 rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
                      A senha deve ter pelo menos 8 caracteres, com letras e números.
                    </div>
                     <div>
                      <label className="block text-sm font-medium leading-6 text-gray-900">Senha atual <span className="text-red-500">*</span></label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <input
                          type={passwordVisibility.current_password ? "text" : "password"}
                          required
                          autoComplete="current-password"
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))}
                          className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("current_password")}
                            className="text-gray-400 transition hover:text-gray-600 focus:outline-none"
                            aria-label={passwordVisibility.current_password ? "Ocultar senha atual" : "Exibir senha atual"}
                          >
                            {passwordVisibility.current_password ? (
                              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium leading-6 text-gray-900">Nova Senha <span className="text-red-500">*</span></label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <input
                          type={passwordVisibility.new_password ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))}
                          className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("new_password")}
                            className="text-gray-400 transition hover:text-gray-600 focus:outline-none"
                            aria-label={passwordVisibility.new_password ? "Ocultar nova senha" : "Exibir nova senha"}
                          >
                            {passwordVisibility.new_password ? (
                              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium leading-6 text-gray-900">Confirmar Nova Senha <span className="text-red-500">*</span></label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <input
                          type={passwordVisibility.confirm_password ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
                          className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility("confirm_password")}
                            className="text-gray-400 transition hover:text-gray-600 focus:outline-none"
                            aria-label={passwordVisibility.confirm_password ? "Ocultar confirmação de senha" : "Exibir confirmação de senha"}
                          >
                            {passwordVisibility.confirm_password ? (
                              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                            ) : (
                              <EyeIcon className="h-5 w-5" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                       <button
                          type="submit"
                          disabled={isSubmittingPassword}
                          className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto disabled:opacity-70 transition-colors"
                        >
                          {isSubmittingPassword ? "Salvando..." : "Salvar Nova Senha"}
                        </button>
                    </div>
                  </form>
                )}
               </div>
            </div>
          ) : (
             <div className="py-8 text-center text-gray-500">Nenhum dado encontrado.</div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
